// ============================================================
// PROJO GROUP — Landing Page (Mobile Fixed)
// FIX: Full mobile responsive layout
// FIX: Navbar — removed Coverage, kept Services/Shop/Rides
// FIX: Logo fits correctly — not cut off
// FIX: TikTok added to social links
// FIX: Sign In always visible on mobile
// ============================================================
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CONTACT, BRAND, VEHICLE_INFO, formatFare, ACTIVE_ZONES, FUTURE_ZONES } from "../utils/constants";
import { useAuth } from "../context/AuthContext";
import PanicButton from "../components/panic/PanicButton";

const G   = "#e8b84b";
const RED = "#8B1A1A";
const RUSTENBURG = { lat: -25.667, lng: 27.242 };
const TAGLINE = "Rustenburg's Own. Ride. Shop. Deliver & Services.";

const goldIcon = new L.DivIcon({
  html: `<div style="width:12px;height:12px;background:#e8b84b;border-radius:50%;border:2px solid #c49a2f;box-shadow:0 0 8px rgba(232,184,75,0.6)"></div>`,
  iconSize:[12,12], iconAnchor:[6,6], className:"",
});
const dimIcon = new L.DivIcon({
  html: `<div style="width:8px;height:8px;background:#3a3a3a;border-radius:50%;border:1px solid #555"></div>`,
  iconSize:[8,8], iconAnchor:[4,4], className:"",
});

function Eyebrow({ text }) {
  return <div style={{ fontSize:"11px", fontWeight:"700", color:G, letterSpacing:"2px",
    textTransform:"uppercase", textAlign:"center", marginBottom:"0.75rem" }}>{text}</div>;
}
function SectionTitle({ children }) {
  return <h2 style={{ fontFamily:"'Syne',sans-serif", fontSize:"clamp(1.6rem,4vw,2.6rem)",
    fontWeight:"800", textAlign:"center", marginBottom:"0.75rem",
    color:"#f5ede8" }}>{children}</h2>;
}
function SectionSub({ children }) {
  return <p style={{ fontSize:"14px", color:"#b8a09a", textAlign:"center",
    marginBottom:"2.5rem", maxWidth:"520px", margin:"0 auto 2.5rem", lineHeight:1.7 }}>{children}</p>;
}

const AREA_DISTANCES = {
  "Rustenburg CBD":0, "Waterfall East":4, "Boitekong":6,
  "Tlhabane":7, "Cashan":3, "Protea Park":5,
  "Rustenburg Industrial":5, "Phokeng":9,
  "Swartruggens":65, "Brits":90,
  "Sun City / Pilanesberg":58, "Johannesburg":145,
};

function calcFare(pickup, dropoff, vehicleMult=1.0) {
  const d1 = AREA_DISTANCES[pickup]  || 0;
  const d2 = AREA_DISTANCES[dropoff] || 0;
  const maxDist = Math.max(d1, d2);
  if (maxDist <= 8) {
    const fare = 60 * vehicleMult;
    return { fare, label:`R${fare % 1===0 ? fare.toFixed(0) : fare.toFixed(2)} — Flat Rate`, zone:1 };
  } else {
    const fare = Math.max(60, maxDist * 7.5) * vehicleMult;
    return { fare, label:`R${fare.toFixed(2)} — ${maxDist}km × R7.50/km`, zone:2 };
  }
}

function BookingWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const areas = Object.keys(AREA_DISTANCES);
  const [pickup,  setPickup]  = useState("Rustenburg CBD");
  const [dropoff, setDropoff] = useState("Boitekong");
  const [vehicle, setVehicle] = useState("ECONOMY");

  const mults = { ECONOMY:1.0, COMFORT:1.3, XL:1.5, LUXURY:2.5 };
  const result = calcFare(pickup, dropoff, mults[vehicle]||1.0);

  const sel = {
    width:"100%", background:"#1c0f0f", border:"1px solid rgba(232,184,75,0.2)",
    borderRadius:"8px", padding:"11px 14px", color:"#f5ede8", fontSize:"14px",
    fontFamily:"'DM Sans',sans-serif", cursor:"pointer",
  };

  return (
    <div style={{ background:"#120808", border:"1px solid rgba(232,184,75,0.2)",
      borderRadius:"20px", padding:"1.5rem", maxWidth:"540px", margin:"0 auto",
      fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }}>
      <div style={{ display:"flex", flexDirection:"column", gap:"12px", marginBottom:"1rem" }}>
        <select style={sel} value={pickup} onChange={e=>setPickup(e.target.value)}>
          {areas.map(a=><option key={a}>{a}</option>)}
        </select>
        <select style={sel} value={dropoff} onChange={e=>setDropoff(e.target.value)}>
          {areas.map(a=><option key={a}>{a}</option>)}
        </select>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
          {["ECONOMY","COMFORT","XL","LUXURY"].map(v=>(
            <button key={v} onClick={()=>setVehicle(v)} style={{
              flex:1, minWidth:"70px", padding:"8px 4px", borderRadius:"8px", cursor:"pointer",
              border:`1px solid ${vehicle===v ? G : "rgba(232,184,75,0.15)"}`,
              background: vehicle===v ? "rgba(232,184,75,0.1)" : "#1c0f0f",
              color: vehicle===v ? G : "#7a5a55", fontSize:"12px", fontWeight:"700",
            }}>{VEHICLE_INFO[v]?.emoji} {v}</button>
          ))}
        </div>
      </div>
      <div style={{ background:"rgba(232,184,75,0.08)", border:`1px solid rgba(232,184,75,0.2)`,
        borderRadius:"12px", padding:"1rem", textAlign:"center", marginBottom:"1rem" }}>
        <div style={{ fontSize:"11px", color:"#7a5a55", marginBottom:"4px" }}>Estimated Fare</div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2.2rem",
          fontWeight:"800", color:G }}>{formatFare(result.fare)}</div>
        <div style={{ fontSize:"12px", color:"#b8a09a", marginTop:"4px" }}>{result.label}</div>
      </div>
      <button onClick={()=>{ user ? navigate("/book") : navigate("/login"); }} style={{
        width:"100%", background:G, color:"#1a0808", border:"none",
        borderRadius:"10px", padding:"14px", fontSize:"15px", fontWeight:"800", cursor:"pointer",
      }}>{user ? "Book Now →" : "Sign In to Book →"}</button>
    </div>
  );
}


// ── PROJO SUPER APP COLLAGE BACKGROUND ──────────────────────
// Only the background changes — everything else untouched
function CollageBackground() {
  const items = [
    // Row 1 — top left cluster
    { emoji:"📚", size:72, x:2,   y:8,   rot:-12, opacity:0.22, blur:0,   scale:1 },
    { emoji:"🎓", size:56, x:8,   y:4,   rot:8,   opacity:0.18, blur:1,   scale:0.9 },
    { emoji:"🤖", size:64, x:14,  y:12,  rot:-5,  opacity:0.20, blur:0,   scale:1 },
    // Top middle
    { emoji:"✈️", size:80, x:35,  y:3,   rot:15,  opacity:0.20, blur:0,   scale:1.1 },
    { emoji:"🌍", size:60, x:44,  y:7,   rot:-8,  opacity:0.18, blur:1,   scale:0.95 },
    { emoji:"📱", size:52, x:52,  y:2,   rot:5,   opacity:0.20, blur:0,   scale:1 },
    // Top right cluster
    { emoji:"🎵", size:68, x:72,  y:5,   rot:-10, opacity:0.22, blur:0,   scale:1 },
    { emoji:"🎮", size:74, x:80,  y:9,   rot:12,  opacity:0.20, blur:1,   scale:1.05 },
    { emoji:"🏉", size:76, x:90,  y:4,   rot:-15, opacity:0.22, blur:0,   scale:1 },
    // Left side
    { emoji:"🔧", size:58, x:1,   y:28,  rot:10,  opacity:0.20, blur:0,   scale:1 },
    { emoji:"📦", size:64, x:5,   y:38,  rot:-8,  opacity:0.22, blur:1,   scale:0.95 },
    { emoji:"🚖", size:78, x:0,   y:50,  rot:5,   opacity:0.20, blur:0,   scale:1 },
    { emoji:"📍", size:52, x:7,   y:62,  rot:-12, opacity:0.22, blur:0,   scale:1 },
    { emoji:"💼", size:60, x:2,   y:72,  rot:8,   opacity:0.18, blur:1,   scale:0.9 },
    // Right side
    { emoji:"🛍️", size:70, x:92,  y:25,  rot:-10, opacity:0.22, blur:0,   scale:1 },
    { emoji:"🚚", size:80, x:88,  y:38,  rot:6,   opacity:0.20, blur:1,   scale:1.05 },
    { emoji:"⭐", size:54, x:94,  y:50,  rot:-5,  opacity:0.22, blur:0,   scale:1 },
    { emoji:"🎰", size:66, x:90,  y:62,  rot:12,  opacity:0.18, blur:1,   scale:0.95 },
    { emoji:"☕", size:52, x:95,  y:73,  rot:-8,  opacity:0.20, blur:0,   scale:1 },
    // Center left
    { emoji:"❤️", size:58, x:18,  y:25,  rot:-15, opacity:0.22, blur:0,   scale:1 },
    { emoji:"💕", size:48, x:22,  y:35,  rot:10,  opacity:0.20, blur:1,   scale:0.9 },
    { emoji:"💬", size:62, x:15,  y:44,  rot:-6,  opacity:0.22, blur:0,   scale:1 },
    { emoji:"🍔", size:60, x:20,  y:56,  rot:8,   opacity:0.20, blur:1,   scale:1 },
    { emoji:"📰", size:66, x:14,  y:68,  rot:-10, opacity:0.18, blur:0,   scale:0.95 },
    // Center right
    { emoji:"💻", size:72, x:75,  y:22,  rot:8,   opacity:0.20, blur:1,   scale:1 },
    { emoji:"🎁", size:56, x:80,  y:35,  rot:-12, opacity:0.22, blur:0,   scale:1 },
    { emoji:"🌟", size:50, x:76,  y:48,  rot:6,   opacity:0.20, blur:1,   scale:0.9 },
    { emoji:"🏠", size:64, x:82,  y:60,  rot:-8,  opacity:0.22, blur:0,   scale:1 },
    { emoji:"📊", size:58, x:77,  y:72,  rot:10,  opacity:0.18, blur:1,   scale:0.95 },
    // Bottom cluster
    { emoji:"🎧", size:68, x:25,  y:78,  rot:-10, opacity:0.22, blur:0,   scale:1 },
    { emoji:"🚀", size:62, x:38,  y:82,  rot:15,  opacity:0.20, blur:1,   scale:1.05 },
    { emoji:"💳", size:54, x:50,  y:85,  rot:-6,  opacity:0.22, blur:0,   scale:1 },
    { emoji:"🌺", size:58, x:62,  y:80,  rot:8,   opacity:0.18, blur:1,   scale:0.9 },
    { emoji:"🏆", size:64, x:72,  y:84,  rot:-12, opacity:0.20, blur:0,   scale:1 },
    // Mid-center scattered
    { emoji:"🔑", size:46, x:32,  y:20,  rot:20,  opacity:0.16, blur:1,   scale:0.85 },
    { emoji:"💡", size:50, x:60,  y:18,  rot:-15, opacity:0.18, blur:0,   scale:0.9 },
    { emoji:"🛡️", size:52, x:42,  y:72,  rot:10,  opacity:0.16, blur:1,   scale:0.88 },
    { emoji:"🎯", size:48, x:56,  y:75,  rot:-8,  opacity:0.18, blur:0,   scale:0.92 },
  ];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:0, pointerEvents:"none", overflow:"hidden" }}>
      {/* Dark overlay for readability */}
      <div style={{ position:"absolute", inset:0, background:"rgba(13,5,5,0.6)", zIndex:1 }} />
      
      {/* Subtle vignette */}
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, transparent 30%, rgba(13,5,5,0.5) 100%)", zIndex:2 }} />

      {/* Collage items */}
      {items.map((item, i) => (
        <div key={i} style={{
          position:"absolute",
          left:`${item.x}%`,
          top:`${item.y}%`,
          fontSize:`${item.size}px`,
          transform:`rotate(${item.rot}deg) scale(${item.scale})`,
          opacity:item.opacity,
          filter:`blur(${item.blur}px) drop-shadow(0 4px 12px rgba(232,184,75,0.15))`,
          zIndex:0,
          userSelect:"none",
          transition:"opacity 0.3s",
          lineHeight:1,
        }}>
          {item.emoji}
        </div>
      ))}

      {/* Glowing accent orbs */}
      <div style={{ position:"absolute", width:"300px", height:"300px", borderRadius:"50%", background:"radial-gradient(circle, rgba(232,184,75,0.04) 0%, transparent 70%)", top:"10%", left:"5%", zIndex:0 }} />
      <div style={{ position:"absolute", width:"400px", height:"400px", borderRadius:"50%", background:"radial-gradient(circle, rgba(139,0,0,0.05) 0%, transparent 70%)", top:"40%", right:"5%", zIndex:0 }} />
      <div style={{ position:"absolute", width:"250px", height:"250px", borderRadius:"50%", background:"radial-gradient(circle, rgba(232,184,75,0.03) 0%, transparent 70%)", bottom:"10%", left:"30%", zIndex:0 }} />
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function handleInstall() {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => setInstallPrompt(null));
    } else {
      setShowInstallModal(true);
    }
  }
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, []);

  const sec  = (bg) => ({ background:bg, padding:"4rem 1rem", borderTop:"1px solid rgba(232,184,75,0.08)" });
  const inner = { maxWidth:"1100px", margin:"0 auto" };
  const homeRoute = !user ? "/login" : user.role==="DRIVER" ? "/driver" : user.role==="ADMIN" ? "/admin" : user.role==="SECURITY" ? "/panic-monitor" : "/book";

  return (
    <div style={{ background:"#0d0505", color:"#f5ede8", fontFamily:"'DM Sans',sans-serif", minHeight:"100vh", overflowX:"hidden", position:"relative" }}>
      <CollageBackground />
      <PanicButton top="78px" />

      {/* ── NAV ── */}
      <nav style={{
        position:"fixed", top:0, left:0, right:0, zIndex:1000,
        height:"64px", display:"flex", alignItems:"center",
        justifyContent:"space-between", padding:"0 1rem",
        background: scrolled ? "rgba(13,5,5,0.98)" : "rgba(13,5,5,0.85)",
        borderBottom:"1px solid rgba(232,184,75,0.12)",
        backdropFilter:"blur(16px)", transition:"background .3s",
      }}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:"8px", cursor:"pointer", flexShrink:0 }}
          onClick={()=>navigate("/")}>
          <div style={{
            width:"46px", height:"46px", flexShrink:0,
            border:"2px solid #c49a2f", borderRadius:"50%",
            boxShadow:"0 0 10px rgba(232,184,75,0.4)", overflow:"hidden",
          }}>
            <img src="/assets/logo/PROJO_LOGO.png" alt="PROJO"
              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
              onError={e=>{e.target.style.display="none"; e.target.parentElement.style.background="radial-gradient(circle at 35% 35%,#f5d078,#e8b84b,#c49a2f,#9a7520)";}} />
          </div>
          <span style={{ fontFamily:"'Syne',sans-serif", fontSize:"14px",
            fontWeight:"800", color:G, letterSpacing:"1px" }}>PROJO GROUP</span>
        </div>

        {/* Desktop nav links */}
        {!isMobile && (
          <div style={{ display:"flex", gap:"1.5rem", alignItems:"center" }}>
            <Link to="/shop"
              style={{ color:"#b8a09a", fontSize:"13px", fontWeight:"600", textDecoration:"none" }}
              onMouseOver={e=>e.target.style.color=G} onMouseOut={e=>e.target.style.color="#b8a09a"}>
              Shop
            </Link>
            <a href="#pricing"
              style={{ color:"#b8a09a", fontSize:"13px", fontWeight:"600", textDecoration:"none" }}
              onMouseOver={e=>e.target.style.color=G} onMouseOut={e=>e.target.style.color="#b8a09a"}>
              Pricing
            </a>
            <a href="#contact"
              style={{ color:"#b8a09a", fontSize:"13px", fontWeight:"600", textDecoration:"none" }}
              onMouseOver={e=>e.target.style.color=G} onMouseOut={e=>e.target.style.color="#b8a09a"}>
              Contact
            </a>
          </div>
        )}

        {/* Right buttons */}
        <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
          {user ? (
            <button onClick={()=>navigate(homeRoute)} style={{
              background:G, color:"#1a0808", border:"none", borderRadius:"8px",
              padding:"9px 16px", fontSize:"13px", fontWeight:"700", cursor:"pointer",
            }}>Go to App →</button>
          ) : (
            <>
              <button onClick={()=>navigate("/login")} style={{
                background:"transparent", color:G,
                border:"1px solid rgba(232,184,75,0.3)",
                borderRadius:"8px", padding:"9px 14px", fontSize:"13px",
                fontWeight:"700", cursor:"pointer",
              }}>Sign In</button>
              {!isMobile && (
                <button onClick={()=>navigate("/register")} style={{
                  background:G, color:"#1a0808", border:"none", borderRadius:"8px",
                  padding:"9px 16px", fontSize:"13px", fontWeight:"700", cursor:"pointer",
                }}>Register</button>
              )}
            </>
          )}
          {/* Hamburger on mobile */}
          {isMobile && (
            <button onClick={()=>setMobileMenu(!mobileMenu)} style={{
              background:"transparent", border:"1px solid rgba(232,184,75,0.2)",
              borderRadius:"8px", width:"40px", height:"40px", cursor:"pointer",
              display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", gap:"5px",
            }}>
              <span style={{ width:"18px", height:"2px", background:"#a8a49e", borderRadius:"2px", display:"block" }} />
              <span style={{ width:"18px", height:"2px", background:"#a8a49e", borderRadius:"2px", display:"block" }} />
              <span style={{ width:"18px", height:"2px", background:"#a8a49e", borderRadius:"2px", display:"block" }} />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile menu drawer */}
      {mobileMenu && isMobile && (
        <>
          <div onClick={()=>setMobileMenu(false)} style={{
            position:"fixed", inset:0, zIndex:998, background:"rgba(0,0,0,0.7)",
          }} />
          <div style={{
            position:"fixed", top:"64px", right:0, bottom:0, width:"260px",
            zIndex:999, background:"#0d0505", borderLeft:"1px solid rgba(232,184,75,0.15)",
            padding:"1rem", fontFamily:"'DM Sans',sans-serif",
          }}>
            {[
              { label:"🛍️ Shop", href:"/shop", external:false },
              { label:"💰 Pricing", href:"#pricing", external:false },
              { label:"📞 Contact", href:"#contact", external:false },
            ].map(item => item.external ? (
              <a key={item.label} href={item.href} target="_blank" rel="noreferrer"
                onClick={()=>setMobileMenu(false)}
                style={{ display:"block", padding:"14px", color:"#c8b8b0",
                  textDecoration:"none", fontSize:"16px", borderRadius:"10px",
                  borderLeft:"3px solid transparent" }}>
                {item.label}
              </a>
            ) : (
              <a key={item.label} href={item.href} onClick={()=>setMobileMenu(false)}
                style={{ display:"block", padding:"14px", color:"#c8b8b0",
                  textDecoration:"none", fontSize:"16px", borderRadius:"10px",
                  borderLeft:"3px solid transparent" }}>
                {item.label}
              </a>
            ))}
            <div style={{ height:"1px", background:"rgba(232,184,75,0.1)", margin:"12px 0" }} />
            {!user && (
              <button onClick={()=>{ setMobileMenu(false); navigate("/register"); }} style={{
                width:"100%", background:G, color:"#1a0808", border:"none",
                borderRadius:"10px", padding:"14px", fontSize:"15px",
                fontWeight:"700", cursor:"pointer",
              }}>Register Free</button>
            )}
          </div>
        </>
      )}

      {/* ── HERO ── */}
      <section style={{ minHeight:"100vh", display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", textAlign:"center",
        padding:"90px 1rem 60px",
        background:"radial-gradient(ellipse 80% 60% at 50% 20%, rgba(139,26,26,0.2) 0%, #0d0505 65%)" }}>

        {/* Logo — fixed to not cut off */}
        <div style={{
          width:"100px", height:"100px",
          overflow:"hidden", marginBottom:"1.5rem",
          border:"3px solid #c49a2f", borderRadius:"50%",
          boxShadow:"0 0 30px rgba(232,184,75,0.5)",
          flexShrink:0,
        }}>
          <img src="/assets/logo/PROJO_LOGO.png" alt="PROJO GROUP"
            style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
            onError={e=>{
              e.target.style.display="none";
              e.target.parentElement.style.background="radial-gradient(circle at 35% 35%,#f5d078,#e8b84b,#c49a2f,#9a7520)";
              e.target.parentElement.innerHTML='<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:Syne,sans-serif;font-size:14px;font-weight:800;color:#2a1a00">PROJO</div>';
            }} />
        </div>

        <div style={{ display:"inline-flex", alignItems:"center", gap:"6px",
          background:"rgba(139,26,26,0.2)", border:"1px solid rgba(232,184,75,0.2)",
          borderRadius:"50px", padding:"6px 14px", marginBottom:"1.25rem" }}>
          <span style={{ width:"6px", height:"6px", borderRadius:"50%", background:"#4ade80" }} />
          <span style={{ fontSize:"12px", color:"#b8a09a", fontWeight:"600" }}>Live in Rustenburg</span>
        </div>

        <h1 style={{ fontFamily:"'Syne',sans-serif",
          fontSize:"clamp(2rem,8vw,4rem)",
          fontWeight:"800", lineHeight:1.1,
          marginBottom:"1rem", letterSpacing:"-1px",
          background:`linear-gradient(135deg, #f5d078, ${G}, #c49a2f)`,
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
          backgroundClip:"text", padding:"0 0.5rem",
        }}>
          Rustenburg's<br/>Own App
        </h1>

        <p style={{ fontSize:"clamp(13px,3.5vw,16px)", color:"#b8a09a",
          maxWidth:"480px", lineHeight:1.7, marginBottom:"2rem", padding:"0 0.5rem" }}>
          {TAGLINE}
        </p>

        {/* Download App button */}
        <button onClick={() => { if (installPrompt) { installPrompt.prompt(); } else { setShowInstallModal(true); } }} style={{
          display:"inline-flex", alignItems:"center", gap:"8px",
          background:`linear-gradient(135deg, ${G}, #c49a2f)`,
          color:"#1a0808", border:"none", borderRadius:"50px",
          padding:"12px 28px", fontSize:"14px", fontWeight:"800",
          cursor:"pointer", marginBottom:"1rem",
          boxShadow:`0 4px 20px rgba(232,184,75,0.4)`,
          letterSpacing:"0.5px",
        }}>
          📲 DOWNLOAD OUR APP
        </button>

        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap",
          justifyContent:"center", marginBottom:"3rem", padding:"0 1rem" }}>
          <button onClick={()=>navigate("/login")} style={{
            background:G, color:"#1a0808", border:"none", borderRadius:"10px",
            padding:"14px 28px", fontSize:"15px", fontWeight:"800", cursor:"pointer",
          }}>Book a Ride →</button>
          <button onClick={()=>navigate("/shop")} style={{
            background:"transparent", color:G,
            border:"1px solid rgba(232,184,75,0.3)",
            borderRadius:"10px", padding:"14px 28px",
            fontSize:"15px", fontWeight:"700", cursor:"pointer",
          }}>Browse Services</button>
          <button onClick={()=>navigate("/driver/signup")} style={{
            background:"transparent", color:"#4ade80",
            border:"1px solid rgba(74,222,128,0.3)",
            borderRadius:"10px", padding:"14px 28px",
            fontSize:"15px", fontWeight:"700", cursor:"pointer",
          }}>🚗 Drive with PROJO</button>
        </div>

        <div style={{ width:"100%", maxWidth:"560px", padding:"0 0.5rem" }}>
          <BookingWidget />
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" style={sec("#120808")}>
        <div style={inner}>
          <Eyebrow text="What We Offer" />
          <SectionTitle>All PROJO Services</SectionTitle>
          <SectionSub>Book anything — rides, deliveries, cleaning, maintenance and more.</SectionSub>
          <div style={{ display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",
            gap:"10px" }}>
            {[
              ["/assets/categories/projo-rides.png", "Rides",          "R60 flat",          "in-app"],
              ["/assets/categories/cleaning.png",          "Cleaning",             "Quote"],
              ["/assets/categories/maintenance.png",       "Maintenance",          "R450 callout"],
              ["/assets/categories/painting.png",          "Painting",             "R28/sqm"],
              ["/assets/categories/cctv.png",              "CCTV",                 "Quote"],
              ["/assets/categories/locksmith.png",         "Locksmith",            "From R200"],
              ["/assets/categories/pest-control.png",      "Pest Control",         "Quote"],
              ["/assets/categories/runners-deliveries.png","Runners & Deliveries", "From R60"],
              ["/assets/categories/pc-console-repair.png", "PC & Console Repair",  "Quote"],
              ["/assets/categories/laundry.png",           "Laundry Services",     "From R19/kg"],
              ["/assets/categories/web-app-development.png","Web & App Dev",       "From R2100"],
              ["/assets/categories/digital-marketing.png", "Digital Marketing",    "From R240"],
              ["/assets/categories/products-shop.png",     "Products",             "Shop"],
            ].map(([img,name,price,type])=>(
              <div key={name} onClick={()=>type==="in-app" ? navigate("/login") : navigate("/shop")} style={{
                background:"#1c0f0f", border:"1px solid rgba(232,184,75,0.12)",
                borderRadius:"14px", padding:"1.25rem 1rem", textAlign:"center",
                cursor:"pointer", transition:"all .2s",
              }}
              onMouseOver={e=>e.currentTarget.style.borderColor=G}
              onMouseOut={e=>e.currentTarget.style.borderColor="rgba(232,184,75,0.12)"}>
                <img src={img} alt={name} style={{
                  width:"52px", height:"52px", borderRadius:"50%", objectFit:"cover",
                  marginBottom:"8px", boxShadow:"0 0 10px rgba(232,184,75,0.3)"
                }} onError={e=>e.target.style.display="none"} />
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"12px",
                  fontWeight:"700", color:"#f5ede8", marginBottom:"4px" }}>{name}</div>
                <div style={{ fontSize:"11px", color:"#7a5a55" }}>{price}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"2rem" }}>
            <button onClick={()=>navigate("/shop")} style={{
              background:G, color:"#1a0808", border:"none", borderRadius:"10px",
              padding:"14px 32px", fontSize:"15px", fontWeight:"700", cursor:"pointer",
            }}>View All Services & Book</button>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={sec("#0d0505")}>
        <div style={inner}>
          <Eyebrow text="Transparent Pricing" />
          <SectionTitle>Simple, Fair Fares</SectionTitle>
          <div style={{ display:"grid",
            gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
            gap:"16px", maxWidth:"700px", margin:"0 auto" }}>
            {[
              { zone:"Zone 1", area:"Inside Rustenburg", fare:"R60 flat", desc:"Rustenburg CBD, Waterfall, Boitekong, Tlhabane, Cashan, Protea Park, Industrial, Phokeng", color:G },
              { zone:"Zone 2", area:"Outside Rustenburg", fare:"R7.50/km", desc:"Swartruggens, Brits, Sun City, Johannesburg, Pretoria and beyond", color:"#b8a09a" },
            ].map(z=>(
              <div key={z.zone} style={{ background:"#120808",
                border:`1px solid ${z.color === G ? "rgba(232,184,75,0.3)" : "rgba(232,184,75,0.1)"}`,
                borderRadius:"16px", padding:"1.5rem" }}>
                <div style={{ fontSize:"11px", fontWeight:"700", color:z.color,
                  letterSpacing:"1px", textTransform:"uppercase", marginBottom:"8px" }}>{z.zone}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2rem",
                  fontWeight:"800", color:z.color, marginBottom:"6px" }}>{z.fare}</div>
                <div style={{ fontSize:"12px", fontWeight:"600", color:"#f5ede8",
                  marginBottom:"8px" }}>{z.area}</div>
                <div style={{ fontSize:"12px", color:"#7a5a55", lineHeight:1.6 }}>{z.desc}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign:"center", fontSize:"12px", color:"#7a5a55", marginTop:"1.5rem" }}>
            Peak hours (06:00–09:00, 16:00–19:00): 15% surge · Vehicle upgrades available
          </p>
        </div>
      </section>


      {/* ── LOYALTY REWARDS ── */}
      <section id="loyalty" style={sec("#0d0505")}>
        <div style={inner}>
          <Eyebrow text="Family Rewards Program" />
          <SectionTitle>Loyal Family Rewards</SectionTitle>
          <SectionSub>
            Collect Loyalty Points for actions you already take and unlock rewards tailored to your journey with PROJO GROUP.
            Just by signing up you open up a world of new possibilities only available to our sign up Customers!
          </SectionSub>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
            gap:"16px", maxWidth:"900px", margin:"0 auto" }}>

            {/* Starter */}
            <div style={{ background:"#120808", border:"1px solid rgba(232,184,75,0.2)",
              borderRadius:"20px", padding:"1.75rem", display:"flex",
              flexDirection:"column", gap:"0" }}>
              <div style={{ fontSize:"11px", fontWeight:"700", color:"#7a5a55",
                letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:"8px" }}>Starter</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2rem",
                fontWeight:"800", color:G, marginBottom:"2px" }}>500 pts</div>
              <div style={{ fontSize:"13px", color:"#7a5a55", marginBottom:"1.25rem" }}>
                R0.00 / Monthly
              </div>
              <div style={{ height:"1px", background:"rgba(232,184,75,0.1)", marginBottom:"1.25rem" }} />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", flex:1, marginBottom:"1.5rem" }}>
                {["5% Discount","Monthly insights","Basic rewards","Priority support"].map(f=>(
                  <div key={f} style={{ display:"flex", gap:"8px", alignItems:"center",
                    fontSize:"13px", color:"#b8a09a" }}>
                    <span style={{ color:G, fontWeight:"700", fontSize:"12px" }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={()=>navigate("/login")} style={{
                width:"100%", background:"transparent", color:G,
                border:"1px solid rgba(232,184,75,0.3)", borderRadius:"10px",
                padding:"12px", fontSize:"13px", fontWeight:"700", cursor:"pointer",
              }}>Get Started →</button>
            </div>

            {/* Growth — highlighted */}
            <div style={{ background:"linear-gradient(135deg,rgba(232,184,75,0.1),rgba(232,184,75,0.03))",
              border:"2px solid rgba(232,184,75,0.5)", borderRadius:"20px",
              padding:"1.75rem", display:"flex", flexDirection:"column",
              position:"relative" }}>
              <div style={{ position:"absolute", top:"-12px", left:"50%",
                transform:"translateX(-50%)", background:G, color:"#1a0808",
                fontSize:"10px", fontWeight:"800", padding:"4px 14px",
                borderRadius:"50px", letterSpacing:"1px", whiteSpace:"nowrap" }}>
                MOST POPULAR
              </div>
              <div style={{ fontSize:"11px", fontWeight:"700", color:G,
                letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:"8px" }}>Growth</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2rem",
                fontWeight:"800", color:G, marginBottom:"2px" }}>1000 pts</div>
              <div style={{ fontSize:"13px", color:"#7a5a55", marginBottom:"1.25rem" }}>
                R0.00 / Monthly
              </div>
              <div style={{ height:"1px", background:"rgba(232,184,75,0.2)", marginBottom:"1.25rem" }} />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", flex:1, marginBottom:"1.5rem" }}>
                {["10% Discount","Exclusive events","Free Coupons","Advanced rewards","Priority Support"].map(f=>(
                  <div key={f} style={{ display:"flex", gap:"8px", alignItems:"center",
                    fontSize:"13px", color:"#b8a09a" }}>
                    <span style={{ color:G, fontWeight:"700", fontSize:"12px" }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={()=>navigate("/login")} style={{
                width:"100%", background:G, color:"#1a0808", border:"none",
                borderRadius:"10px", padding:"12px", fontSize:"13px",
                fontWeight:"800", cursor:"pointer",
              }}>Reach for Growth →</button>
            </div>

            {/* Elite */}
            <div style={{ background:"#120808", border:"1px solid rgba(139,26,26,0.4)",
              borderRadius:"20px", padding:"1.75rem", display:"flex", flexDirection:"column" }}>
              <div style={{ fontSize:"11px", fontWeight:"700", color:"#a82020",
                letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:"8px" }}>Elite</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2rem",
                fontWeight:"800", color:G, marginBottom:"2px" }}>1500 pts</div>
              <div style={{ fontSize:"13px", color:"#7a5a55", marginBottom:"8px" }}>
                R0.00 / Support R25.00 Monthly
              </div>
              <div style={{ background:"rgba(139,26,26,0.15)", border:"1px solid rgba(139,26,26,0.3)",
                borderRadius:"10px", padding:"10px 12px", marginBottom:"1.25rem",
                fontSize:"12px", color:"#b8a09a", lineHeight:1.6 }}>
                ❤️ Fan & Supporter of our <strong style={{color:G}}>"Take my Hand"</strong> initiative —
                helping Children and the Poor with Necessities, Food and Guidance.
                Follow us on social media to see what we love to do!
              </div>
              <div style={{ height:"1px", background:"rgba(139,26,26,0.2)", marginBottom:"1.25rem" }} />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", flex:1, marginBottom:"1.5rem" }}>
                {["15% Discount","VIP access","Personal concierge","Support members only",
                  "Early access + up to 25% Discounts"].map(f=>(
                  <div key={f} style={{ display:"flex", gap:"8px", alignItems:"center",
                    fontSize:"13px", color:"#b8a09a" }}>
                    <span style={{ color:"#a82020", fontWeight:"700", fontSize:"12px" }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={()=>navigate("/login")} style={{
                width:"100%", background:"rgba(139,26,26,0.2)", color:G,
                border:"1px solid rgba(139,26,26,0.4)", borderRadius:"10px",
                padding:"12px", fontSize:"13px", fontWeight:"700", cursor:"pointer",
              }}>Fan / Supporter →</button>
            </div>

          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" style={sec("#120808")}>
        <div style={{ ...inner, textAlign:"center" }}>
          <Eyebrow text="Get In Touch" />
          <SectionTitle>Contact PROJO GROUP</SectionTitle>
          <p style={{ color:"#7a5a55", fontSize:"13px", marginBottom:"2rem" }}>
            Rustenburg, North West Province, South Africa
          </p>
          <div style={{ display:"flex", gap:"10px", justifyContent:"center", flexWrap:"wrap", padding:"0 1rem" }}>
            {[
              { label:"💬 WhatsApp",  href:CONTACT.whatsappLink, bg:"#25D366", color:"#fff" },
              { label:"📘 Facebook",  href:CONTACT.facebook,     bg:"#1877F2", color:"#fff" },
              { label:"📷 Instagram", href:CONTACT.instagram,    bg:"#E1306C", color:"#fff" },
              { label:"🎵 TikTok",   href:CONTACT.tiktok,       bg:"#010101", color:"#fff" },
              { label:"✉️ Email",   href:`mailto:${CONTACT.email}`, bg:"#120808", color:G },
            ].map(b=>(
              <a key={b.label} href={b.href} target="_blank" rel="noreferrer" style={{
                display:"inline-flex", alignItems:"center", gap:"6px",
                background:b.bg, color:b.color, textDecoration:"none",
                borderRadius:"10px", padding:"11px 18px", fontSize:"13px", fontWeight:"700",
                border:"1px solid rgba(232,184,75,0.1)",
              }}>{b.label}</a>
            ))}
            <button onClick={()=>navigate("/shop")} style={{
              display:"inline-flex", alignItems:"center", gap:"6px",
              background:G, color:"#1a0808", border:"1px solid rgba(232,184,75,0.1)",
              borderRadius:"10px", padding:"11px 18px", fontSize:"13px", fontWeight:"700",
              cursor:"pointer",
            }}>🛍️ Shop</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background:"#080303", borderTop:"1px solid rgba(232,184,75,0.1)",
        padding:"2.5rem 1rem 2rem", fontFamily:"'DM Sans',sans-serif" }}>
        <div style={inner}>
          {/* Mobile: stacked, Desktop: grid */}
          <div style={{ display:"grid",
            gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",
            gap:"2rem", marginBottom:"2rem" }}>

            {/* Brand */}
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"0.75rem" }}>
                <div style={{ width:"40px", height:"40px", borderRadius:"50%",
                  overflow:"hidden", border:"2px solid #c49a2f", borderRadius:"50%", flexShrink:0 }}>
                  <img src="/assets/logo/PROJO_LOGO.png" alt="PROJO"
                    style={{ width:"100%", height:"100%", objectFit:"contain" }}
                    onError={e=>e.target.style.display="none"} />
                </div>
                <div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"14px",
                    fontWeight:"800", color:G }}>PROJO GROUP</div>
                  <div style={{ fontSize:"10px", color:"#7a5a55" }}>Est. 2023</div>
                </div>
              </div>
              <div style={{ fontSize:"12px", color:"#7a5a55", lineHeight:2 }}>
                📞 {CONTACT.phone}<br/>
                ✉️ {CONTACT.email}<br/>
                🌐 www.projogroup.co.za
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 style={{ fontSize:"12px", fontWeight:"700", color:"#b8a09a",
                letterSpacing:"1px", textTransform:"uppercase", marginBottom:"1rem" }}>Services</h4>
              {["Rides","Courier","Cleaning","Maintenance","Painting","CCTV",
                "Pest Control","Web & App","Marketing","Locksmith","Runners","PC & Console Repair"].map(s=>(
                <button key={s} onClick={()=>navigate(s==="Rides" ? "/login" : "/shop")}
                  style={{ display:"block", background:"none", border:"none", padding:0,
                  fontSize:"12px", color:"#7a5a55", textDecoration:"none", marginBottom:"5px",
                  cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif" }}
                  onMouseOver={e=>e.target.style.color=G}
                  onMouseOut={e=>e.target.style.color="#7a5a55"}>{s}</button>
              ))}
            </div>

            {/* Connect */}
            <div>
              <h4 style={{ fontSize:"12px", fontWeight:"700", color:"#b8a09a",
                letterSpacing:"1px", textTransform:"uppercase", marginBottom:"1rem" }}>Connect</h4>
              {[
                ["WhatsApp",  CONTACT.whatsappLink],
                ["Facebook",  CONTACT.facebook],
                ["Instagram", CONTACT.instagram],
                ["TikTok",    CONTACT.tiktok],
                ["Shop",      "/shop"],
                ["Website",   CONTACT.website],
              ].map(([l,href])=> href.startsWith("/") ? (
                <button key={l} onClick={()=>navigate(href)}
                  style={{ display:"block", background:"none", border:"none", padding:0,
                  fontSize:"12px", color:"#7a5a55", textDecoration:"none", marginBottom:"6px",
                  cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif" }}
                  onMouseOver={e=>e.target.style.color=G}
                  onMouseOut={e=>e.target.style.color="#7a5a55"}>{l}</button>
              ) : (
                <a key={l} href={href} target="_blank" rel="noreferrer"
                  style={{ display:"block", fontSize:"12px", color:"#7a5a55",
                  textDecoration:"none", marginBottom:"6px" }}
                  onMouseOver={e=>e.target.style.color=G}
                  onMouseOut={e=>e.target.style.color="#7a5a55"}>{l}</a>
              ))}
            </div>
          </div>

          <div style={{ borderTop:"1px solid rgba(232,184,75,0.08)", paddingTop:"1.5rem",
            textAlign:"center" }}>
            <div style={{ fontSize:"12px", color:"#3d1a1a" }}>
              © 2023–2026 PROJO GROUP. All rights reserved. Proudly Rustenburg.
            </div>
          </div>
        </div>
      </footer>

      {/* Install Guide Modal */}
      {showInstallModal && (
        <>
          <div onClick={() => setShowInstallModal(false)} style={{
            position:"fixed", inset:0, zIndex:2000, background:"rgba(0,0,0,0.85)",
          }} />
          <div style={{
            position:"fixed", bottom:0, left:0, right:0, zIndex:2001,
            background:"#120808", borderTop:"2px solid rgba(232,184,75,0.3)",
            borderRadius:"20px 20px 0 0", padding:"1.5rem 1.25rem 2.5rem",
            fontFamily:"'DM Sans',sans-serif",
          }}>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:"1.25rem" }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.1rem",
                fontWeight:"800", color:"#e8b84b" }}>📲 Install PROJO GROUP</div>
              <button onClick={() => setShowInstallModal(false)} style={{
                background:"none", border:"none", color:"#6b6760",
                fontSize:"22px", cursor:"pointer" }}>✕</button>
            </div>
            <div style={{ background:"rgba(74,222,128,0.06)",
              border:"1px solid rgba(74,222,128,0.2)", borderRadius:"14px",
              padding:"1rem", marginBottom:"10px" }}>
              <div style={{ fontSize:"13px", fontWeight:"700", color:"#4ade80",
                marginBottom:"10px" }}>🤖 Android (Chrome)</div>
              <div style={{ display:"flex", gap:"10px", marginBottom:"6px", fontSize:"13px", color:"#b8a09a" }}>
                <span style={{ color:"#60a5fa", fontWeight:"700", flexShrink:0 }}>1.</span>
                Open Safari and visit{" "}
                <a href="https://app.projogroup.co.za" target="_blank" rel="noreferrer"
                  style={{ color:"#60a5fa", fontWeight:"700" }}>app.projogroup.co.za</a>
              </div>
              {["Tap the 3-dot menu at top right","Tap: Add to Home screen","Tap: Add to confirm"].map((s,i) => (
                <div key={i} style={{ display:"flex", gap:"10px", marginBottom:"6px",
                  fontSize:"13px", color:"#b8a09a" }}>
                  <span style={{ color:"#4ade80", fontWeight:"700", flexShrink:0 }}>{i+1}.</span>{s}
                </div>
              ))}
            </div>
            <div style={{ background:"rgba(96,165,250,0.06)",
              border:"1px solid rgba(96,165,250,0.2)", borderRadius:"14px",
              padding:"1rem", marginBottom:"1.25rem" }}>
              <div style={{ fontSize:"13px", fontWeight:"700", color:"#60a5fa",
                marginBottom:"10px" }}>🍎 iPhone (Safari)</div>
              {[null,
                "Tap the Share button (□↑) at the bottom",
                "Tap: Add to Home Screen",
                "Tap: Add — icon appears on home screen!"].map((s,i) => (
                <div key={i} style={{ display:"flex", gap:"10px", marginBottom:"6px",
                  fontSize:"13px", color:"#b8a09a" }}>
                  <span style={{ color:"#60a5fa", fontWeight:"700", flexShrink:0 }}>{i+1}.</span>{s}
                </div>
              ))}
            </div>
            <button onClick={() => setShowInstallModal(false)} style={{
              width:"100%", background:"#e8b84b", color:"#1a0808", border:"none",
              borderRadius:"10px", padding:"13px", fontSize:"14px",
              fontWeight:"700", cursor:"pointer",
            }}>Got it!</button>
          </div>
        </>
      )}
    </div>
  );
}
